// @flow
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toastr } from 'react-redux-toastr';
import FileSaver from 'file-saver';
import { generateStandardFilename } from '@fortress-technology-solutions/fortress-component-library/utils';

import useSelectedProperty from '../../hooks/useSelectedProperty';
import useUserOrganizationId from '../../hooks/useUserOrganizationId';
import useIntl from '../../hooks/useIntl';
import RenewalOfferService from '../../services/renewalOfferService';
import ResidentService from '../../services/residentService';
import messages from './messages';
import { MAX_OFFERS } from './RenewalOfferForm/helpers';

const renewalOfferService = new RenewalOfferService();
const residentService = new ResidentService();

function parseOffer(offer) {
  const {
    offer1Rent,
    offer2Rent,
    offer3Rent,
    offer1Term,
    offer2Term,
    offer3Term,
    offer1TermId,
    offer2TermId,
    offer3TermId,
    offerDate,
    offerExpirationDate,
    leaseOfferTransactions,
  } = offer;

  const lotx = leaseOfferTransactions?.map((lot) => ({
    transactionCode: lot.transactionCode,
    offer1amount: lot.offer1amount === 0 ? '---' : lot.offer1amount,
    offer2amount: lot.offer2amount === 0 ? '---' : lot.offer2amount,
    offer3amount: lot.offer3amount === 0 ? '---' : lot.offer3amount,
  }));

  const offer1Length = offer1Term?.masterLeaseTerm?.nMonths ?? '---';
  const offer2Length = offer2Term?.masterLeaseTerm?.nMonths ?? '---';
  const offer3Length = offer3Term?.masterLeaseTerm?.nMonths ?? '---';

  // pricingSources has one entry per provider-owned tier; index by tier for a plain lookup below.
  const providerNameByTier = (offer.pricingSources ?? []).reduce(
    (acc, source) => ({ ...acc, [source.tier]: source.providerName }),
    {},
  );

  return {
    offerDate,
    offerExpirationDate,
    offer1Rent,
    offer1TermId,
    offer2Rent: offer2Rent || '---',
    offer3Rent: offer3Rent || '---',
    offer1TermLength:
      offer1Length === '---' ? offer1Length : `${offer1Length} Months`,
    offer2TermLength:
      offer2Length === '---' ? offer2Length : `${offer2Length} Months`,
    offer3TermLength:
      offer3Length === '---' ? offer3Length : `${offer3Length} Months`,
    offer2TermId: offer2TermId || '---',
    offer3TermId: offer3TermId || '---',
    leaseOfferTransactions: lotx,
    offer1ProviderName: providerNameByTier[1] ?? null,
    offer2ProviderName: providerNameByTier[2] ?? null,
    offer3ProviderName: providerNameByTier[3] ?? null,
  };
}

export function buildOfferPayload(formValues, unitId) {
  const { transactionCodes, ...rest } = formValues;

  const payload = {
    ...rest,
    unitId,
    offerDate: new Date(),
    // Filter out the default placeholder row — backend rejects id: 'default'
    transactionCodes: (transactionCodes ?? []).filter(
      (tc) => tc.id !== 'default',
    ),
  };

  // Omit optional offer fields entirely when empty — Joi.string() rejects null/''
  // Offer 1 is required; offers 2..MAX_OFFERS are optional
  for (let i = 2; i <= MAX_OFFERS; i++) {
    const rentKey = `offer${i}Rent`;
    const termKey = `offer${i}TermId`;
    if (!payload[rentKey]) delete payload[rentKey];
    if (!payload[termKey]) delete payload[termKey];
  }

  return payload;
}

export function useGenerateRenewalOffer({
  residentId,
  history,
  propertyTransactionCodes,
  onGetPropertyTransactionCodes,
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const selectedProperty = useSelectedProperty();
  const organizationId = useUserOrganizationId();
  const propertyId = selectedProperty?.id;
  const { rebaRevMgmt, renewalOfferLetterRows } = useFlags();

  // Fetch transaction codes if not already loaded
  useEffect(() => {
    if (propertyTransactionCodes.length === 0) {
      onGetPropertyTransactionCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch resident data ────────────────────────────────────────────────────
  const {
    data: residentData,
    isLoading: isLoadingResident,
    error: residentError,
  } = useQuery(
    ['resident', organizationId, propertyId, residentId],
    async () => {
      const response = await residentService.getResident(
        organizationId,
        propertyId,
        residentId,
      );
      return response;
    },
    {
      enabled:
        Boolean(organizationId) && Boolean(propertyId) && Boolean(residentId),
    },
  );

  const residentInfo = residentData?.residentInfo ?? {};
  // A failed refetch keeps the data it already had. The page shows the load error only when it
  // has no resident to show, so a transient failure cannot replace a half-filled form.
  const residentLoadFailed =
    Boolean(residentError) && residentData === undefined;
  const customer = residentInfo?.residentCustomer?.customer ?? {};
  const residentName = customer.firstName
    ? `${customer.firstName} ${customer.lastName}`
    : '';
  const units = residentInfo?.lease?.units ?? [];
  const unit = units.length === 1 ? units[0] : { id: '', number: '' };
  const leaseId = residentInfo?.leaseId ?? '';

  // ── Fetch lease terms ──────────────────────────────────────────────────────
  const {
    data: leaseTermsData,
    isLoading: isLoadingTerms,
    error: termsError,
  } = useQuery(
    ['leaseTerms', organizationId, propertyId],
    async () => {
      return renewalOfferService.getAllLeaseTerms(organizationId, propertyId);
    },
    { enabled: Boolean(organizationId) && Boolean(propertyId) },
  );

  const leaseTermOptions = leaseTermsData ?? [];
  // Terms are required too: without them the Term dropdown is empty and Submit never enables.
  const termsLoadFailed = Boolean(termsError) && leaseTermsData === undefined;
  const loadFailed = residentLoadFailed || termsLoadFailed;

  // ── Fetch previous offers (enabled once leaseId is known) ─────────────────
  const { data: previousOffersRaw, isLoading: isLoadingOffers } = useQuery(
    ['previousRenewalOffers', organizationId, propertyId, leaseId],
    async () => {
      return renewalOfferService.getAllPreviousRenewalOffers(
        organizationId,
        propertyId,
        leaseId,
      );
    },
    {
      enabled:
        Boolean(organizationId) && Boolean(propertyId) && Boolean(leaseId),
    },
  );

  const previousOffers = (previousOffersRaw ?? [])
    .reduce((acc, offer) => {
      if (!offer.offer1Rent || !offer.offer1Term || !offer.offer1TermId) {
        return acc;
      }
      return [...acc, parseOffer(offer)];
    }, [])
    .reverse();

  // ── Fetch REBA renewal pricing (ENG-30334) ────────────────────────────────
  // Only fetched when `reba-rev-mgmt` is on. The endpoint is safe to call regardless — it
  // returns `{ hasPricing: false, prices: [] }` when the flag is off server-side — but gating
  // the query here keeps the flag-off path byte-for-byte as it was, with no extra request.
  const {
    data: rebaPricingData,
    isLoading: isLoadingRebaPricing,
    isFetched: isFetchedRebaPricing,
  } = useQuery(
    ['renewalPricing', organizationId, propertyId, leaseId],
    async () => {
      return renewalOfferService.getRenewalPricing(
        organizationId,
        propertyId,
        leaseId,
      );
    },
    {
      enabled:
        Boolean(rebaRevMgmt) &&
        Boolean(organizationId) &&
        Boolean(propertyId) &&
        Boolean(leaseId),
    },
  );

  // Keyed by PropertyLeaseTerm id, which is exactly what the form's term dropdown binds to
  // (`offerNTermId`), so a tier can look up its own price without re-deriving a month count.
  // Only AUTHORITATIVE entries are kept: a term REBA has no price for, or prices at zero, must
  // leave the field editable and un-prefilled — see the API's `isAuthoritative` for why zero is
  // deliberately not authoritative.
  const rebaPricingByTermId = (rebaPricingData?.prices ?? []).reduce(
    (acc, price) =>
      price?.isAuthoritative && price?.termId
        ? { ...acc, [price.termId]: price.amount }
        : acc,
    {},
  );

  // Companion to rebaPricingByTermId, same keying and authoritative-only filter.
  const rebaProviderNameByTermId = (rebaPricingData?.prices ?? []).reduce(
    (acc, price) =>
      price?.isAuthoritative && price?.termId
        ? { ...acc, [price.termId]: price?.provider?.name ?? null }
        : acc,
    {},
  );
  // ENG-31380 — answered by the same endpoint that returns the pricing. False by default, which is
  // also what the flag-off path wants: the query is disabled and never answers.
  const isPartnerEnabled = Boolean(rebaPricingData?.isPartnerEnabled);

  // react-query v3 reports isLoading only while a query has never held data, so a refetch with
  // cached data never gates. The resident and the terms are required: any load of either with
  // no data, the first one or a retry after a failure, holds the form back. Pricing is
  // optional, so only its first-ever load gates; a retry after a failed first load must not
  // unmount a form the user is filling in. A disabled query (flag off, or no lease) is idle,
  // so it never gates.
  const isInitialLoading =
    isLoadingResident ||
    isLoadingTerms ||
    (isLoadingRebaPricing && !isFetchedRebaPricing);

  // Once the form has shown for this resident it stays. A query that becomes enabled later,
  // such as the pricing query when reba-rev-mgmt turns on while the page is open, starts a
  // first-ever load, and that must not unmount a form the user is filling in. The
  // settle-after-mount effect in RenewalOfferForm/hooks.js picks up the late answer. The
  // banner state does not release the gate, so a retry after a failed load still shows the
  // spinner. A new resident id gates again, because its own loads have not run.
  const [releasedResidentId, setReleasedResidentId] = useState(null);
  const showsForm = !isInitialLoading && !loadFailed;
  useEffect(() => {
    if (showsForm) setReleasedResidentId(residentId);
  }, [showsForm, residentId]);
  const isFormGated = isInitialLoading && releasedResidentId !== residentId;

  // ── Create offer mutation ─────────────────────────────────────────────────
  const { mutateAsync: submitOffer } = useMutation(
    async (offerPayload) => {
      const response = await renewalOfferService.createOffer(
        organizationId,
        propertyId,
        offerPayload,
        leaseId,
      );
      return response;
    },
    {
      onSuccess: async (response) => {
        toastr.success(
          intl.formatMessage(messages.success),
          intl.formatMessage(messages.createRenewalOfferSuccessDescription),
        );

        await queryClient.invalidateQueries([
          'previousRenewalOffers',
          organizationId,
          propertyId,
          leaseId,
        ]);

        const pdf = await renewalOfferService.downloadOffer(response.id);
        const fileName = generateStandardFilename({
          type: 'Lease-Renewal-Offer',
          descriptor: selectedProperty?.name ?? '',
          extension: 'pdf',
        });
        FileSaver.saveAs(pdf, fileName);
      },
      onError: (err) => {
        toastr.error(
          intl.formatMessage(messages.error),
          err?.message ?? String(err),
        );
      },
    },
  );

  const handleSubmit = async (formValues) => {
    await submitOffer(buildOfferPayload(formValues, unit?.id));
  };

  const parseLeaseTerms = (terms) =>
    terms.map((term) => ({
      value: term.id,
      text: `${term.masterLeaseTerm.nMonths} Months`,
    }));

  const goBack = () => {
    history.goBack();
  };

  return {
    residentName,
    unit,
    leaseId,
    leaseTermOptions: parseLeaseTerms(leaseTermOptions),
    previousOffers,
    propertyTransactionCodes,
    isLoadingResident,
    isLoadingTerms,
    isLoadingOffers,
    residentError,
    residentLoadFailed,
    termsLoadFailed,
    loadFailed,
    rebaPricingByTermId,
    rebaProviderNameByTermId,
    // ENG-31244 — property's quote-expiration config, for pre-populating the read-only
    // offer expiration date. See useSelectedProperty above — the container's existing path to
    // property settings.
    quoteExpTime: selectedProperty?.quoteExpTime ?? null,
    isPartnerEnabled,
    // ENG-31380 — react-query v3 reports isLoading only while actually fetching; a DISABLED query
    // is 'idle'. The form must wait for isPartnerEnabled when the query will answer and must NOT
    // when it never will (flag off, or a resident with no lease). The form gate is
    // isFormGated below.
    isLoadingRebaPricing,
    isFormGated,
    // The form renders its transaction list behind this flag, so the loading skeleton must
    // reserve the same height.
    showsTransactionRows: Boolean(renewalOfferLetterRows),
    handleSubmit,
    goBack,
  };
}
